import type {
  CampaignCreationCapabilities,
  SmartCreationBrowserDraft,
  SmartCreationDraft,
  SmartCreationObjectiveRecommendation,
  SmartCreationRecommendationEvidence,
  SmartCreationStrategyOperation,
  SmartCreationStructure,
  SmartCreationStructureOptionsEvaluation,
  SmartCreationStructureRepairAction,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import {
  ObjectivesStep,
  type RecommendationField,
} from "@/components/smart-creation/ObjectivesStep";
import { StrategyStep } from "@/components/smart-creation/StrategyStep";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { ProductsStep } from "./ProductsStep";
import { StructureStep } from "./StructureStep";

interface SmartCreationStepContentProps {
  plan: SmartCreationBrowserDraft;
  draft: SmartCreationDraft;
  mutationPending: boolean;
  objectiveRecommendation: SmartCreationObjectiveRecommendation | null;
  productHistory: SmartCreationRecommendationEvidence | null;
  productHistoryPending: boolean;
  productHistoryError: boolean;
  onRefreshProductHistory: () => void;
  shopId: string;
  shopChanging: boolean;
  pendingShopId: string | null;
  shopTransitionMinimumElapsed: boolean;
  onShopChange: (shopId: string) => Promise<void>;
  capabilities?: CampaignCreationCapabilities;
  enterInput: string;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onCatalogBlockingChange: (blocked: boolean) => void;
  onDraftChange: (draft: SmartCreationDraft, preserveRecommendation?: boolean) => void;
  onEnterInputChange: (value: string) => void;
  onReconnect: () => void;
  recommendationPending: boolean;
  promotionValidationRequested: boolean;
  onRefreshRecommendation: () => void;
  onApplyRecommendation: (field: RecommendationField) => void;
  strategyRecommendationPending: boolean;
  onApplyStrategyOperations: (
    operations: SmartCreationStrategyOperation[],
    expectedRevision: number,
  ) => Promise<boolean>;
  onRefreshStrategyRecommendation: () => void;
  onAcceptStrategyRecommendation: (
    kind: SmartCreationTargetingStrategyKind,
    expectedRevision: number,
  ) => Promise<boolean>;
  strategyEditorEpoch: number;
  onStrategyLocalDirtyChange: (dirty: boolean) => void;
  structureEvaluation?: SmartCreationStructureOptionsEvaluation;
  structureEvaluationLoading: boolean;
  structureEvaluationError: boolean;
  onRetryStructureEvaluation: () => void;
  onStructureChange: (structure: SmartCreationStructure) => void;
  onStructureRepair: (repair: SmartCreationStructureRepairAction) => void;
}

/** Renders the editable wizard step before the preview: products, objectives, strategy or structure. */
export function SmartCreationStepContent({
  plan,
  draft,
  mutationPending,
  objectiveRecommendation,
  productHistory,
  productHistoryPending,
  productHistoryError,
  onRefreshProductHistory,
  shopId,
  shopChanging,
  pendingShopId,
  shopTransitionMinimumElapsed,
  onShopChange,
  capabilities,
  enterInput,
  invalidProducts,
  onInvalidProductsChange,
  onCatalogBlockingChange,
  onDraftChange,
  onEnterInputChange,
  onReconnect,
  recommendationPending,
  promotionValidationRequested,
  onRefreshRecommendation,
  onApplyRecommendation,
  strategyRecommendationPending,
  onApplyStrategyOperations,
  onRefreshStrategyRecommendation,
  onAcceptStrategyRecommendation,
  strategyEditorEpoch,
  onStrategyLocalDirtyChange,
  structureEvaluation,
  structureEvaluationLoading,
  structureEvaluationError,
  onRetryStructureEvaluation,
  onStructureChange,
  onStructureRepair,
}: SmartCreationStepContentProps) {
  if (draft.currentStep === "products") {
    if (!capabilities) return null;
    return (
      <ProductsStep
        draft={draft}
        shopId={shopId}
        shopChanging={shopChanging}
        pendingShopId={pendingShopId}
        shopTransitionMinimumElapsed={shopTransitionMinimumElapsed}
        onShopChange={onShopChange}
        capabilities={capabilities}
        enterInput={enterInput}
        invalidProducts={invalidProducts}
        onInvalidProductsChange={onInvalidProductsChange}
        onCatalogBlockingChange={onCatalogBlockingChange}
        onDraftChange={onDraftChange}
        onEnterInputChange={onEnterInputChange}
        onReconnect={onReconnect}
      />
    );
  }
  if (draft.currentStep === "objectives") {
    if (!capabilities) return null;
    return (
      <ObjectivesStep
        key={`${shopId}:${plan.id}`}
        shopId={shopId}
        draft={draft}
        capabilities={capabilities}
        recommendation={objectiveRecommendation}
        productHistory={productHistory}
        productHistoryPending={productHistoryPending}
        productHistoryError={productHistoryError}
        onRefreshProductHistory={onRefreshProductHistory}
        recommendationPending={recommendationPending}
        promotionValidationRequested={promotionValidationRequested}
        promotionWindowResetKey={plan.id}
        onDraftChange={onDraftChange}
        onRefreshRecommendation={onRefreshRecommendation}
        onApplyRecommendation={onApplyRecommendation}
      />
    );
  }
  if (draft.currentStep === "strategy") {
    if (!capabilities) return null;
    return (
      <StrategyStep
        key={`${plan.id}:${strategyEditorEpoch}`}
        plan={plan}
        capabilities={capabilities}
        mutationPending={mutationPending}
        recommendationPending={strategyRecommendationPending}
        onApplyOperations={onApplyStrategyOperations}
        onRefreshRecommendation={onRefreshStrategyRecommendation}
        onAcceptRecommendation={onAcceptStrategyRecommendation}
        onLocalDirtyChange={onStrategyLocalDirtyChange}
      />
    );
  }
  if (draft.currentStep === "structure") {
    return (
      <StructureStep
        plan={plan}
        structure={draft.structure}
        evaluation={structureEvaluation}
        mutationPending={mutationPending}
        loading={structureEvaluationLoading}
        error={structureEvaluationError}
        onRetry={onRetryStructureEvaluation}
        onStructureChange={onStructureChange}
        onRepair={onStructureRepair}
      />
    );
  }
  return null;
}
